import { Box, Button, CircularProgress, IconButton, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import FormListCell from 'components/list/form-list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import NumberField from 'components/number-field';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import { AlertTriangle, Edit, MinusCircle, PlusCircle } from 'lucide-react';
import React, { useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import ProductAutocomplete from 'views/order-feed/features/my-supply-plan/components/product-autocomplete';
import SelectOrderDate from 'views/order-feed/features/my-supply-plan/components/select-order-date';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { REPEAT_ORDER_ANCHOR_ID } from 'views/order-feed/features/my-supply-plan/utils/create-order-utils';
import { getSelectedTransportCompany, getSiteTransportCompanies } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';

type Props = {
  createOrderIsLoading: boolean;
  handleCreateOrder: (data: CreateOrderFormValues) => void;
};

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan';
const PAGE_NAME_ID_ALERT = 'orderFeed.alert';

const OrderLineListRow = styled(ListRow)(({ theme }) => ({
  '& > td': {
    padding: theme.spacing(2),
  },
}));

const CreateOrderForm: React.FC<Props> = ({ handleCreateOrder, createOrderIsLoading }) => {
  const intl = useIntl();
  const theme = useTheme();
  const params = useParams<MySupplyPlanParams>();
  const { account } = useAccountState();
  const {
    dispatchDatesQuery: { data: dispatchSchedule, isLoading: isDispatchScheduleLoading },
  } = useOrderFeedState();
  const {
    customerAssortmentQuery: { data: customerAssortment, isLoading: productsIsLoading },
  } = useMySupplyPlanState();
  const { createOrderFeedEvent } = useGoogleTagManager();
  const orderDetailsDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region FORMS

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { isValid, errors },
  } = useFormContext<CreateOrderFormValues>();

  const { fields, remove, prepend } = useFieldArray({
    control,
    name: 'orderLines',
    shouldUnregister: false,
  });

  const watchOrderLines = useWatch({
    control,
    name: 'orderLines',
    defaultValue: fields,
  });

  // #region HELPERS

  const availableProducts = customerAssortment?.availableProducts ?? [];
  const deliveryMethods = getSiteTransportCompanies(account.company?.addressAccess, params.globalSiteId) ?? [];

  const hasProductDateRangeError = (idx: number) => errors.orderLines?.at?.(idx)?.itemNumber?.type === 'valid';

  const orderLineCellSx = { padding: isMobile ? theme.spacing(4, 0, 0, 0) : 0 };

  const handleAddOrderLine = () => {
    createOrderFeedEvent('add_order_line', {
      supplyPlanType: SupplyPlanType.OFD1,
    });

    prepend({
      itemNumber: '',
      orderQuantity: 0,
    });
  };

  const handleRemoveOrderLine = (idx: number) => {
    createOrderFeedEvent('remove_order_line', {
      supplyPlanType: SupplyPlanType.OFD1,
    });

    remove(idx);
  };

  const handleEditOrderDetails = () => {
    createOrderFeedEvent('edit_order_details', {
      supplyPlanType: SupplyPlanType.OFD1,
    });
  };

  // #endregion HELPERS

  if (productsIsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box id={REPEAT_ORDER_ANCHOR_ID} display="flex" flexDirection="column" gap={4}>
      <Typography color="common.white">
        <FormattedMessage id="orderFeed.listOrderSupplyPlan.createOrderDialog.title" />
      </Typography>
      <Divider />
      <form onSubmit={handleSubmit(handleCreateOrder)}>
        <List isLoading={productsIsLoading} options={[]}>
          {fields.map((field, idx) => (
            <OrderLineListRow key={`order_row_${field.id}_${idx}`}>
              <ListCell align="center">
                {hasProductDateRangeError(idx) ? (
                  <Tooltip
                    arrow
                    title={
                      <Box flexDirection="column" alignItems="flex-start" display="flex">
                        <FormattedMessage id={`${PAGE_NAME_ID_ALERT}.productDateRange`} />
                        {availableProducts
                          .find((s) => s.itemNumber === watchOrderLines[idx]?.itemNumber)
                          ?.validityRanges.map((s, idx) => (
                            <small key={s.validFrom + idx}>
                              <FormattedDate value={s.validFrom} /> - <FormattedDate value={s.validTo} />
                            </small>
                          ))}
                      </Box>
                    }>
                    <AlertTriangle color={theme.palette.warning.light} size={18} />
                  </Tooltip>
                ) : null}
              </ListCell>

              <FormListCell sx={orderLineCellSx}>
                {idx === 0 && <SelectOrderDate shippingSchedule={dispatchSchedule} isLoading={isDispatchScheduleLoading} />}
              </FormListCell>
              <FormListCell sx={orderLineCellSx}>
                <ProductAutocomplete idx={idx} products={customerAssortment} />
              </FormListCell>
              <FormListCell sx={orderLineCellSx} align="left">
                <Box
                  display="flex"
                  justifyContent={{ xs: 'space-between', md: 'flex-start' }}
                  flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                  alignItems="center">
                  <NumberField
                    dark
                    fullWidth
                    sx={{
                      minWidth: 120,
                    }}
                    disableDecimal
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.totalOrderQtyLabel` })}
                    onChange={() => trigger(`orderLines.${idx}.orderQuantity`)}
                    controller={{
                      name: `orderLines.${idx}.orderQuantity`,
                      control,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                        },
                      },
                    }}
                    disabled={createOrderIsLoading}
                    endAdornment={'kg'}
                  />
                </Box>
              </FormListCell>
              <ListCell sx={{ margin: isMobile ? theme.spacing(0, 0, 4, 0) : 0 }}>
                <Box display="flex" justifyContent={isMobile ? 'space-between' : 'flex-start'}>
                  {idx === 0 ? (
                    <>
                      <IconButton
                        color="primary"
                        disabled={createOrderIsLoading || watchOrderLines.length <= 1}
                        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.removeOrderLine` })}
                        onClick={() => handleRemoveOrderLine(idx)}>
                        <MinusCircle size={46} strokeWidth={1} />
                      </IconButton>
                      <IconButton
                        color="primary"
                        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.addOrderLine` })}
                        disabled={createOrderIsLoading}
                        onClick={handleAddOrderLine}>
                        <PlusCircle size={46} strokeWidth={1} />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      color="primary"
                      disabled={createOrderIsLoading}
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.removeOrderLine` })}
                      onClick={() => handleRemoveOrderLine(idx)}>
                      <MinusCircle size={46} strokeWidth={1} />
                    </IconButton>
                  )}
                  {idx === 0 ? (
                    <Dialog
                      ref={orderDetailsDialogRef}
                      maxWidth="sm"
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.editOrderDetails` })}
                      button={
                        <IconButton
                          color="primary"
                          sx={{
                            justifyContent: isMobile ? 'flex-end' : 'none',
                            width: 62,
                            height: 62,
                          }}
                          disabled={createOrderIsLoading}
                          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.editOrderDetails` })}
                          size="small"
                          onClick={handleEditOrderDetails}>
                          <Edit size={46} strokeWidth={1} />
                        </IconButton>
                      }>
                      <FlexBox>
                        <TextField
                          dark
                          fullWidth
                          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.customerReference` })}
                          maxLength={20}
                          controller={{
                            name: 'customerOrderReference',
                            control,
                          }}
                        />
                        {deliveryMethods.length !== 1 && (
                          <Select
                            dark
                            fullWidth
                            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.methodOfDelivery` })}
                            controller={{
                              name: 'transportMethod',
                              control,
                              rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                            }}
                            onChange={(event) => {
                              const selectedTransportCompany = getSelectedTransportCompany(
                                deliveryMethods,
                                String(event.target.value)
                              );
                              setValue('addressId', selectedTransportCompany?.name ?? '');
                            }}>
                            {deliveryMethods.map((transport) => (
                              <RadioSelectItem key={transport.key} value={transport.key}>
                                {transport.key} - {transport.name}
                              </RadioSelectItem>
                            ))}
                          </Select>
                        )}
                        <TextField
                          multiline
                          dark
                          fullWidth
                          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.orderComment` })}
                          controller={{
                            name: 'orderComment',
                            control,
                          }}
                        />
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            sx={{
                              marginTop: isMobile ? 4 : 0,
                            }}
                            type="button"
                            onClick={() => orderDetailsDialogRef.current?.setOpen(false)}>
                            <FormattedMessage id="general.save" />
                          </Button>
                        </Box>
                      </FlexBox>
                    </Dialog>
                  ) : null}
                </Box>
              </ListCell>

              <ListCell sx={{ padding: 0 }} align="center">
                {idx === 0 && (
                  <Button
                    fullWidth={isMobile}
                    type="submit"
                    disabled={createOrderIsLoading || !isValid}
                    sx={{ whiteSpace: 'nowrap' }}
                    startIcon={createOrderIsLoading ? <CircularProgress color="inherit" size={18} /> : null}>
                    <FormattedMessage id="general.create" values={{ type: '' }} />
                  </Button>
                )}
              </ListCell>
            </OrderLineListRow>
          ))}
        </List>
      </form>
    </Box>
  );
};

export default CreateOrderForm;
